import { useState } from "react"

import cn from "classnames"
import { isSameDay, lastDayOfMonth, set } from "date-fns"
import { default as ReactCalendar } from "react-calendar"

import LoadingSkeleton from "components/LoadingSkeleton"
import useAvailability from "hooks/useAvailability"

import ValidatingWrapper from "../ValidatingWrapper"

import AdminTime from "./AdminTime"
import FrontendTime from "./FrontendTime"

const AvailabilityCalendar: React.FC<{
  variant: "admin" | "frontend"
  userId?: string // <-- For frontend, no userId is supplied & gets all. For staff, they id is supplied via the session. For admin, can specify different user ids on the edit user page
}> = ({ variant, userId }) => {
  // min date is 1 week from now
  const minDate = set(new Date(), { date: new Date().getDate() + 7 })
  // max date is 3 months from now
  const maxDate = set(new Date(), { month: new Date().getMonth() + 3 })
  // default the selected date to the min date
  const [activeDate, setActiveDate] = useState<Date>(minDate)
  // the calendar defaults to the current month so set the activeStartDate to the first day of the month by default
  const [activeStartDate, setActiveStartDate] = useState<Date>(minDate)
  // similarly, set the activeEndDate to the last day of the month just before midnight by default
  const [activeEndDate, setActiveEndDate] = useState<Date>(
    set(lastDayOfMonth(minDate), { hours: 23, minutes: 59, seconds: 59 })
  )
  const { availability, isValidating, isError, bookingInterval, totalStaff } =
    useAvailability({
      start: activeStartDate,
      end: activeEndDate,
      ...(variant === "admin" && userId && { userId })
    })

  const validatingWrapperClass = cn("items-start gap-4", {
    "grid grid-cols-2": variant === "admin",
    "xlmin:grid-cols-2 grid grid-cols-1": variant === "frontend"
  })

  if (isError) return <p>An error has occurred. Please reload.</p>
  if (!availability || !availability.length)
    return <LoadingSkeleton className="w-full h-full aspect-video" />

  return (
    <ValidatingWrapper
      isValidating={isValidating}
      className={validatingWrapperClass}
    >
      <div>
        <ReactCalendar
          className="!w-full"
          value={activeDate}
          minDate={minDate}
          maxDate={maxDate}
          minDetail="month"
          showNeighboringMonth={false}
          view="month"
          tileDisabled={({ date }) => {
            const dateIsAvailable = availability.some(
              ({ dateTime }) =>
                isSameDay(dateTime, date) && dateTime > new Date()
            )
            const hasAvailableSlots = availability
              .filter(a => isSameDay(a.dateTime, date))
              .map(a => a.slots)
              .every(slots => slots < 1) // <-- Less than 1 incase of a negative number

            // if is admin, need to disable dates that are not returned from the availability array only
            if (variant === "admin") {
              return !dateIsAvailable
            }

            // if is frontend, disable dates with no slots on top of the dates that are not returned from the availability array
            return !dateIsAvailable || hasAvailableSlots
          }}
          tileClassName={({ date }) => {
            if (variant === "frontend") return ""
            const dateAvailability = availability.filter(a =>
              isSameDay(a.dateTime, date)
            )
            // each user gets 1 slot per time but the busyTimes api returns it in an array. If the length is 1, then the user has marked themselves as busy already
            const isBusy = dateAvailability.every(
              a => a?.busyTimes?.length === 1
            )
            const isPartiallyBusy = dateAvailability.some(
              a => a?.busyTimes?.length === 1
            )
            const isBooked = dateAvailability.every(a => a.slots < 1)
            const isPartiallyBooked = dateAvailability.some(
              a => a?.bookings?.length !== 0
            )

            if (dateAvailability.length === 0) return ""

            return cn({
              "bg-error text-white hover:!bg-error-dark": isBusy,
              "bg-error text-white bg-opacity-50 hover:!bg-error-dark":
                isPartiallyBusy && !isBusy,
              "bg-warning text-white hover:!bg-warning-dark": isBooked,
              "bg-warning text-white bg-opacity-50 hover:!bg-warning-dark":
                isPartiallyBooked && !isBooked
            })
          }}
          onClickDay={e => setActiveDate(e)}
          onActiveStartDateChange={e => {
            setActiveStartDate(e.activeStartDate)
            setActiveEndDate(
              set(lastDayOfMonth(e.activeStartDate), {
                hours: 23,
                minutes: 59,
                seconds: 59
              })
            )
          }}
        />
        {variant === "admin" && (
          <p className="!mt-3">
            <strong>Total staff:</strong> {totalStaff}
          </p>
        )}
      </div>
      <div>
        <ul className="no-prose-list my-0">
          {availability.length > 0 ? (
            availability.map(a => {
              if (
                isSameDay(a.dateTime, activeDate) &&
                a.dateTime > new Date()
              ) {
                if (variant === "admin") {
                  return (
                    <AdminTime
                      key={a.dateTime.toISOString()}
                      availability={a}
                      bookingInterval={bookingInterval}
                      userId={userId}
                    />
                  )
                } else {
                  return (
                    <FrontendTime
                      key={a.dateTime.toISOString()}
                      availability={a}
                    />
                  )
                }
              } else {
                return null
              }
            })
          ) : (
            <li className="text-center">
              No availability on this day, please select another
            </li>
          )}
        </ul>
      </div>
    </ValidatingWrapper>
  )
}

export default AvailabilityCalendar
